import { useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Button } from '@/components/ui/button';

interface ZoneData { zone: string; minutes: number; percentage: number; }
interface ZoneDistributionChartProps {
  powerZones?: ZoneData[]; hrZones?: ZoneData[]; paceZones?: ZoneData[];
}

const ZONE_COLORS = ['hsl(215,60%,55%)', 'hsl(190,60%,50%)', 'hsl(140,50%,45%)', 'hsl(40,80%,55%)', 'hsl(0,65%,55%)', 'hsl(330,60%,50%)', 'hsl(270,50%,50%)'];
type MetricType = 'power' | 'hr' | 'pace';

export function ZoneDistributionChart({ powerZones, hrZones, paceZones }: ZoneDistributionChartProps) {
  const available: MetricType[] = useMemo(() => {
    const m: MetricType[] = [];
    if (powerZones?.length) m.push('power');
    if (hrZones?.length) m.push('hr');
    if (paceZones?.length) m.push('pace');
    return m;
  }, [powerZones, hrZones, paceZones]);

  const [active, setActive] = useState<MetricType>(available[0] ?? 'hr');
  const data = useMemo(() => {
    switch (active) { case 'power': return powerZones ?? []; case 'hr': return hrZones ?? []; case 'pace': return paceZones ?? []; default: return []; }
  }, [active, powerZones, hrZones, paceZones]);

  if (!available.length) {
    return (
      <div className="rounded-xl border border-border/60 p-4">
        <h3 className="text-sm font-semibold mb-3">Zone Distribution</h3>
        <div className="h-[260px] flex items-center justify-center text-muted-foreground text-sm">No zone data available yet.</div>
      </div>
    );
  }

  const labels: Record<MetricType, string> = { power: 'Power', hr: 'Heart Rate', pace: 'Pace' };

  return (
    <div className="rounded-xl border border-border/60 p-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold">Zone Distribution</h3>
        <div className="flex items-center gap-1 flex-wrap">
          {available.map((m) => (
            <Button key={m} variant={active === m ? 'default' : 'outline'} size="sm" className="h-7 text-xs px-2.5" onClick={() => setActive(m)}>{labels[m]}</Button>
          ))}
        </div>
      </div>
      <div className="h-[260px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.2} horizontal={false} />
            <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} unit="%" />
            <YAxis dataKey="zone" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} width={30} />
            <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
            <Bar dataKey="percentage" radius={[0, 4, 4, 0]} name="Time in Zone">
              {data.map((_e, i) => <Cell key={i} fill={ZONE_COLORS[i % ZONE_COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
